<script setup>
import { computed, ref, onBeforeUnmount } from 'vue'
import { useMatchMedia } from '@/utils'
import IconBase from '@/components/icon/IconBase.vue'
import { flowSourceList, mediaTypeMeta } from './mediaflowMeta'

/**
 * Hover / keyboard-focus summary of what a scene or break contains: its
 * sources top layer first, as Preview shows them (draft included). Wraps the
 * row; opens after a short pause so moving across the dock doesn't flicker,
 * and only where there is a mouse (taps on touch screens are left alone).
 */
const props = defineProps({
	/** The flow as saved. */
	flow: { type: Object, required: true },
	/** The same flow with its unapplied edits (defaults to the saved one). */
	drafted: { type: Object, default: null },
	/** Footer line, e.g. what a click does. */
	hint: { type: String, default: '' },
	disabled: { type: Boolean, default: false },
	max: { type: Number, default: 8 },
	wrapperClass: { type: String, default: '' },
})

const SHOW_DELAY_MS = 450
const FOCUS_DELAY_MS = 150
const HIDE_DELAY_MS = 80

const canHover = useMatchMedia('(hover: hover) and (pointer: fine)')
const active = computed(() => !props.disabled && canHover.value)
const summary = computed(() => flowSourceList(props.flow, props.drafted || props.flow))
const shownItems = computed(() => summary.value.items.slice(0, props.max))
const more = computed(() => Math.max(0, summary.value.items.length - props.max))

// Placed beside the whole dock row (not just the hovered part), so it never
// covers the row's ⋮ / Cue buttons. floating-vue calls this with the popper as `this`.
function rowAnchor() {
	return this.$el?.closest?.('.mf-row') || this.$el
}

const root = ref(null)
const open = ref(false)
let timer = null
const schedule = (value, ms) => {
	clearTimeout(timer)
	timer = setTimeout(() => { open.value = value }, ms)
}
const onEnter = () => { if (active.value) schedule(true, SHOW_DELAY_MS) }
const onLeave = () => {
	if (root.value?.contains(document.activeElement) && document.activeElement?.matches(':focus-visible')) return
	schedule(false, HIDE_DELAY_MS)
}
const onFocusIn = (event) => {
	if (!active.value || !event.target?.matches?.(':focus-visible')) return
	schedule(true, FOCUS_DELAY_MS)
}
const onFocusOut = (event) => {
	if (root.value?.contains(event.relatedTarget)) return
	schedule(false, HIDE_DELAY_MS)
}
const close = () => { clearTimeout(timer); open.value = false }
onBeforeUnmount(() => clearTimeout(timer))
</script>

<template>
	<div
		ref="root"
		:class="wrapperClass"
		@mouseenter="onEnter"
		@mouseleave="onLeave"
		@focusin="onFocusIn"
		@focusout="onFocusOut"
		@pointerdown="close"
		@keydown.esc="close"
	>
		<VTooltip
			class="flex h-full w-full min-w-0"
			:shown="active && open"
			:triggers="[]"
			:popper-triggers="[]"
			:auto-hide="false"
			:reference-node="rowAnchor"
			placement="right-start"
			:distance="8"
			popper-class="mf-sources-tip"
		>
			<slot />
			<template #popper>
				<div class="w-[264px] text-left" role="tooltip" data-testid="sources-tip">
					<p class="m-0 mb-1.5 flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-surface-7">
						<span class="min-w-0 truncate">{{ flow.name }}</span>
						<span class="shrink-0 font-normal normal-case tracking-normal">· {{ summary.items.length }} {{ summary.items.length === 1 ? 'source' : 'sources' }}</span>
					</p>
					<p v-if="summary.draft" class="m-0 mb-1.5 flex items-center gap-1.5 text-[0.7rem] text-gold" data-testid="sources-tip-draft">
						<span class="size-1.5 shrink-0 rounded-full bg-gold" aria-hidden="true" />Unapplied changes, shown as in Preview<template v-if="summary.removed"> ({{ summary.removed }} removed)</template>
					</p>
					<p v-if="!summary.items.length" class="m-0 text-xs text-surface-8" data-testid="sources-tip-empty">No sources yet</p>
					<ol v-else class="m-0 flex list-none flex-col gap-1 p-0">
						<li v-for="item in shownItems" :key="item.id" class="flex min-w-0 items-center gap-2 text-xs" :data-hidden="item.hidden || undefined" data-testid="sources-tip-item">
							<span class="flex size-5 shrink-0 items-center justify-center rounded" :class="mediaTypeMeta(item.type).tone" :title="mediaTypeMeta(item.type).label">
								<icon-base width="11" height="11" :width-view-box="mediaTypeMeta(item.type).viewBox" :height-view-box="mediaTypeMeta(item.type).viewBox" :fill="mediaTypeMeta(item.type).fill"><component :is="mediaTypeMeta(item.type).icon" /></icon-base>
							</span>
							<span class="min-w-0 flex-1 truncate" :class="item.hidden ? 'text-surface-7 line-through' : 'text-surface-text-2'">{{ item.name }}</span>
							<span v-if="item.change" class="shrink-0 text-[0.65rem] font-medium text-gold">{{ item.change === 'new' ? 'New' : 'Edited' }}</span>
							<span v-if="item.hidden" class="shrink-0 text-[0.65rem] text-surface-7">Hidden</span>
							<span v-else-if="item.muted" class="shrink-0 text-[0.65rem] text-surface-7">Muted</span>
						</li>
						<li v-if="more" class="pl-7 text-[0.7rem] text-surface-7" data-testid="sources-tip-more">+{{ more }} more</li>
					</ol>
					<p v-if="hint" class="m-0 mt-2 text-[0.65rem] text-surface-7">{{ hint }}</p>
				</div>
			</template>
		</VTooltip>
	</div>
</template>

<!-- Global: floating-vue teleports the popper to <body>. Plain dark card, no blur. -->
<style>
.v-popper__popper.mf-sources-tip .v-popper__inner {
	background: var(--surface-1, #0f1424);
	color: var(--surface-text-2, #e7eaf3);
	border: 1px solid var(--surface-4, #2c3550);
	border-radius: 8px;
	padding: 10px 12px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}
.v-popper__popper.mf-sources-tip .v-popper__arrow-container {
	display: none;
}
</style>
